import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useFirebaseAuth } from './FirebaseAuthContext';
import registrationService from '../services/registrationService';

const UserPersonaContext = createContext();

export const useUserPersona = () => {
  const context = useContext(UserPersonaContext);
  if (!context) {
    throw new Error('useUserPersona must be used within a UserPersonaProvider');
  }
  return context;
};

export const UserPersonaProvider = ({ children }) => {
  const { user, getIdToken } = useFirebaseAuth();
  const [profile, setProfile] = useState(null);
  const [skaters, setSkaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState('guest');

  // Load profile and skaters data
  const loadUserData = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setSkaters([]);
      setPersona('guest');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const [profileData, skatersData] = await Promise.all([
        registrationService.getProfile(token).catch(() => null),
        registrationService.getSkaters(token).catch(() => [])
      ]);

      setProfile(profileData);
      setSkaters(skatersData || []);
    } catch (error) {
      console.error('Error loading user data for persona:', error);
      setProfile(null);
      setSkaters([]);
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  // Calculate persona based on user data
  useEffect(() => {
    if (!user) {
      setPersona('guest');
      return;
    }

    if (!profile) {
      setPersona('new_user');
      return;
    }

    // Child skaters only (exclude own profile — isSelfSkater marks the adult’s own record)
    const hasChildSkaters = (skaters || []).some((s) => s.isSelfSkater !== true);
    const isAdultSkater =
      Boolean(profile.isSkater) || Boolean(profile.roles?.includes('skater'));
    // Check both the isCoach toggle AND the roles array (from role approval system)
    const isCoach = Boolean(profile.isCoach) || Boolean(profile.roles?.includes('coach'));
    const isParent =
      Boolean(profile.isParent) || Boolean(profile.roles?.includes('parent'));

    // Priority order for personas:
    // 1. Coach only (no personal skating, no children, not marked as parent)
    if (isCoach && !isAdultSkater && !hasChildSkaters && !isParent) {
      setPersona('coach');
      return;
    }

    // 2. Adult skater with children (non-self skaters on account)
    if (isAdultSkater && hasChildSkaters) {
      setPersona('adult_skater_parent');
      return;
    }

    // 3. Adult skater only (may include own skater profile only)
    if (isAdultSkater && !hasChildSkaters) {
      setPersona('adult_skater');
      return;
    }

    // 4. Parent with children
    if (hasChildSkaters) {
      setPersona('parent');
      return;
    }

    // 5. New user (no skaters, not a skater themselves)
    setPersona('new_user');
  }, [user, profile, skaters]);

  // Load data when user changes
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Get navigation labels based on persona
  const getNavigationLabels = useCallback(() => {
    const labels = {
      adult_skater: {
        profile: 'My Account',
        skaters: 'Skaters',
        skatersIcon: 'Person',
        registration: 'Season Registration'
      },
      adult_skater_parent: {
        profile: 'My Account',
        skaters: 'Skaters',
        skatersIcon: 'Group',
        registration: 'Season Registration'
      },
      parent: {
        profile: 'My Account',
        skaters: 'Skaters',
        skatersIcon: 'Group',
        registration: 'Register Skaters'
      },
      coach: {
        profile: 'My Account',
        skaters: 'Skaters',
        skatersIcon: 'Group',
        registration: 'Registration'
      },
      new_user: {
        profile: 'My Account',
        skaters: 'Add Skaters',
        skatersIcon: 'PersonAdd',
        registration: 'Get Started'
      },
      guest: {
        profile: 'My Account',
        skaters: 'Skaters',
        skatersIcon: 'Group',
        registration: 'Registration'
      }
    };

    return labels[persona] || labels.new_user;
  }, [persona]);

  // Get persona display info
  const getPersonaInfo = useCallback(() => {
    const info = {
      adult_skater: {
        label: 'Adult Skater',
        icon: '⛸️',
        description: 'You are registered as an adult skater'
      },
      adult_skater_parent: {
        label: 'Skater & Parent',
        icon: '⛸️👨‍👩‍👧‍👦',
        description: 'You skate and manage other skaters'
      },
      parent: {
        label: 'Parent/Guardian',
        icon: '👨‍👩‍👧‍👦',
        description: 'You manage skaters'
      },
      coach: {
        label: 'Coach',
        icon: '🏆',
        description: 'You manage skaters as a coach'
      },
      new_user: {
        label: 'New User',
        icon: '👋',
        description: 'Get started by adding skaters'
      },
      guest: {
        label: 'Guest',
        icon: '👤',
        description: 'Sign in to access features'
      }
    };

    return info[persona] || info.guest;
  }, [persona]);

  // Refresh user data (useful after profile updates)
  const refreshUserData = useCallback(async () => {
    await loadUserData();
  }, [loadUserData]);

  const value = {
    persona,
    profile,
    skaters,
    loading,
    getNavigationLabels,
    getPersonaInfo,
    refreshUserData
  };

  return (
    <UserPersonaContext.Provider value={value}>
      {children}
    </UserPersonaContext.Provider>
  );
};

export default UserPersonaContext;
